import { cn } from "./classNames-DcGnTh2N.js";
import { Button } from "./Button-BNkyYU8u.js";
import { cva } from "class-variance-authority";
import { jsx } from "react/jsx-runtime";
import TextareaAutosize from "react-textarea-autosize";
//#region src/components/InputGroup/InputGroupAddon.tsx
const addonVariants = cva([
	"tw:flex",
	"tw:items-center",
	"tw:gap-1",
	"tw:[&_svg.lucide:not([class*='tw:size-'])]:size-4",
	"tw:[&_svg]:shrink-0"
], {
	defaultVariants: { align: "inline-start" },
	variants: { align: {
		"inline-start": [
			"tw:order-first",
			"tw:shrink-0",
			"tw:[&:not(:empty)]:pl-4"
		],
		"inline-end": [
			"tw:order-last",
			"tw:shrink-0",
			"tw:[&:not(:empty)]:pr-4"
		],
		"block-start": [
			"tw:order-first",
			"tw:[&:not(:empty)]:w-full",
			"tw:[&:not(:empty)]:px-4",
			"tw:[&:not(:empty)]:py-1.5"
		],
		"block-end": [
			"tw:order-last",
			"tw:[&:not(:empty)]:w-full",
			"tw:[&:not(:empty)]:px-4",
			"tw:[&:not(:empty)]:py-1.5"
		]
	} }
});
const InputGroupAddon = ({ children, align = "inline-start", className, ...props }) => /* @__PURE__ */ jsx("div", {
	...props,
	"data-align": align,
	className: cn(addonVariants({
		align,
		className
	})),
	children
});
//#endregion
//#region src/components/InputGroup/InputGroupButton.tsx
const InputGroupButton = ({ ref, className, tabIndex = -1, ...props }) => /* @__PURE__ */ jsx(Button, {
	...props,
	tabIndex,
	ref,
	variant: "invisible",
	size: "icon-sm",
	className: cn("tw:size-5", "tw:[&_svg]:text-muted-foreground", className)
});
//#endregion
//#region src/components/InputGroup/InputGroupInput.tsx
const InputGroupInput = ({ ref, className, ...inputProps }) => /* @__PURE__ */ jsx("input", {
	...inputProps,
	ref,
	className: cn("tw:flex-1", "tw:min-w-0", "tw:bg-transparent", "tw:outline-hidden", "tw:font-sans", "tw:text-sm", "tw:placeholder:text-neutral-400", "tw:placeholder:font-normal", "tw:disabled:cursor-not-allowed", className)
});
//#endregion
//#region src/components/InputGroup/InputGroupText.tsx
const InputGroupText = ({ className, ...props }) => /* @__PURE__ */ jsx("span", {
	className: cn("tw:flex", "tw:items-center", "tw:gap-2", "tw:text-sm", "tw:text-muted-foreground", "tw:[&_svg]:pointer-events-none", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-4", className),
	...props
});
//#endregion
//#region src/components/InputGroup/InputGroupTextArea.tsx
const InputGroupTextArea = ({ ref, className, onChange, onValueChange, value, defaultValue, onBlur, onValueBlur, disableResize = false, minRows = 1, maxRows = 4, ...props }) => {
	const handleChange = (event) => {
		onValueChange?.(event.target.value);
		onChange?.(event);
	};
	const handleBlur = (event) => {
		onValueBlur?.(event.target.value);
		onBlur?.(event);
	};
	return /* @__PURE__ */ jsx(TextareaAutosize, {
		...props,
		ref,
		value: value ?? void 0,
		defaultValue: defaultValue ?? void 0,
		onChange: handleChange,
		onBlur: handleBlur,
		minRows,
		maxRows: maxRows === "unlimited" ? void 0 : maxRows,
		className: cn("tw:w-full", "tw:bg-transparent", "tw:outline-hidden", "tw:px-4", "tw:font-sans", "tw:text-sm", "tw:placeholder:text-neutral-400", "tw:placeholder:font-normal", "tw:disabled:cursor-not-allowed", { "tw:resize-none": disableResize }, className)
	});
};
//#endregion
//#region src/components/InputGroup/InputGroup.tsx
const inputGroupVariants = cva([
	"tailwindcss-preflight",
	"tw:group",
	"tw:overflow-hidden",
	"tw:box-border",
	"tw:flex",
	"tw:items-center",
	"tw:min-w-0",
	"tw:rounded-[var(--radius-lg,0.25rem)]",
	"tw:border",
	"tw:border-solid",
	"tw:border-input",
	"tw:bg-muted",
	"tw:text-foreground",
	"tw:transition",
	"tw:has-[:is(input,textarea):focus]:ring-4",
	"tw:has-[:is(input,textarea)[readonly]]:focus-within:ring-0",
	"tw:has-[:is(input,textarea)[readonly]]:border-input",
	"tw:has-[:is(input,textarea)[readonly]:focus]:border-input",
	"tw:has-[:is(input,textarea):disabled]:cursor-not-allowed",
	"tw:has-[:is(input,textarea):disabled]:opacity-disabled",
	"tw:has-[:is(input,textarea)[aria-invalid=true]]:border-destructive",
	"tw:has-[:is(input,textarea)[aria-invalid=true]:focus]:border-destructive",
	"tw:has-[:is(input,textarea)[aria-invalid=true]:focus]:ring-destructive/20",
	"tw:has-[textarea]:items-start",
	"tw:has-[[data-align=block-start]:not(:empty)]:flex-wrap",
	"tw:has-[[data-align=block-end]:not(:empty)]:flex-wrap",
	"tw:[&>input]:has-[[data-align=block-start]:not(:empty)]:w-auto",
	"tw:[&>input]:has-[[data-align=block-start]:not(:empty)]:flex-1",
	"tw:[&>input]:has-[[data-align=block-end]:not(:empty)]:w-auto",
	"tw:[&>input]:has-[[data-align=block-end]:not(:empty)]:flex-1",
	"tw:[&_input]:px-4",
	"tw:[&>input]:has-[[data-align=inline-start]:not(:empty)]:pl-2",
	"tw:[&>input]:has-[[data-align=inline-end]:not(:empty)]:pr-2",
	"tw:[&_:is(input,textarea)]:py-2",
	"tw:[&_:is(input,textarea)]:leading-[18px]",
	"tw:[&_textarea]:!min-h-[calc(2.25rem-2px)]"
], {
	defaultVariants: {
		state: "default",
		focusVariant: "default"
	},
	compoundVariants: [{
		state: "error",
		focusVariant: "on",
		className: ["tw:!border-destructive", "tw:!ring-destructive/20"]
	}, {
		state: "default",
		focusVariant: "off",
		className: ["tw:has-[:is(input,textarea):focus]:border-input"]
	}],
	variants: {
		focusVariant: {
			default: [],
			on: [
				"tw:!border-primary",
				"tw:!ring-ring/50",
				"tw:!ring-4"
			],
			off: ["tw:has-[:is(input,textarea):focus]:!ring-0"]
		},
		state: {
			default: ["tw:has-[:is(input,textarea):focus]:border-primary", "tw:has-[:is(input,textarea):focus]:ring-ring/50"],
			error: [
				"tw:border-destructive",
				"tw:has-[:is(input,textarea):focus]:border-destructive",
				"tw:has-[:is(input,textarea):focus]:ring-destructive/20"
			],
			warning: [
				"tw:border-warning-500",
				"tw:has-[:is(input,textarea):focus]:border-warning-500",
				"tw:has-[:is(input,textarea):focus]:ring-warning/20"
			]
		}
	}
});
const InputGroupRoot = ({ ref, children, state, focusVariant, className, ...props }) => /* @__PURE__ */ jsx("div", {
	...props,
	ref,
	className: cn(inputGroupVariants({
		state,
		focusVariant,
		className
	})),
	children
});
const InputGroup = InputGroupRoot;
InputGroup.Input = InputGroupInput;
InputGroup.Addon = InputGroupAddon;
InputGroup.Button = InputGroupButton;
InputGroup.TextArea = InputGroupTextArea;
InputGroup.Text = InputGroupText;
//#endregion
export { InputGroup };

//# sourceMappingURL=InputGroup-CZv_7QlM.js.map